import {
  CreateVariables,
  FeatureBulkTrashVariables,
  GetVariables,
  UpdateVariables,
  ListingVariables,
  Option,
} from "./../models/app";
import { DataStore, SortDirection } from "aws-amplify";
import { useDispatch } from "react-redux";
import { setListing, setListingAll, setSelected } from "../store/ducks/feature";
import { Feature } from "../models";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import { CreateFeatureInput } from "../models/api";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  async function fetch(params: ListingVariables) {
    const { searchText, startIndex, limit } = params;
    // console.log("searchText:", searchText);

    try {
      const listing = await DataStore.query(
        Feature as any,
        (model: any) => {
          model.deleted("eq", "0");

          if (searchText.length > 0)
            model.name("contains", searchText.toLowerCase());

          return model;
        },
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s) => s.createdAt(SortDirection.DESCENDING),
        }
      );

      return listing;
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function get(params: GetVariables) {
    const { id } = params;

    try {
      const single: Feature | undefined = await DataStore.query(
        Feature as any,
        id
      );

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;

    try {
      const createInput: CreateFeatureInput = {
        precedence: data.precedence,
        name: data.name.toLowerCase(),
        slug: data.slug,
        deleted: "0",
        createdAt: new Date().toLocaleString(),
        createdByID: userID,
        createdByName: userName,
      };

      if (data.icon) createInput.icon = data.icon;
      if (data.parent) createInput.parent = data.parent;
      if (data.private) createInput.private = data.private;

      await DataStore.save(new Feature(createInput as any));

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: UpdateVariables) {
    const { id, data } = params;

    try {
      const original = await get({ id });

      await DataStore.save(
        Feature.copyOf(original!, (updated) => {
          updated.precedence = data.precedence
            ? data.precedence
            : original!.precedence;
          updated.name = data.name ? data.name.toLowerCase() : original!.name;
          updated.slug = data.slug ? data.slug : original!.slug;
          updated.icon = data.icon ? data.icon : original!.icon;
          updated.parent = data.parent ? data.parent : original!.parent;
          updated.private = data.private ? data.private : original!.private;
        })
      );

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: GetVariables) {
    try {
      const original = await get(params);

      await DataStore.save(
        Feature.copyOf(original!, (updated) => {
          updated.deleted = "1";
        })
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: FeatureBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(
      setListing(listing.filter((resource: any) => !ids.has(resource.id)))
    );

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: GetVariables) {
    const { id } = params;
    try {
      await DataStore.delete(id as any);

      // dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  function options(listing: Feature[]) {
    const options: Option[] = [{ label: "None", value: "-1" }];

    for (let option of listing) {
      options.push({ label: option.name, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["name"];

  const api: any = {};

  api[`${listingName}Model`] = Feature as any;
  api[`${listingName}Options`] = options;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: Feature[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeListingAll`] = (listing: Feature[]) =>
    dispatch(setListingAll(listing));
  api[`${listingName}ChangeSelected`] = (id: string) =>
    dispatch(setSelected(id));

  return api;
};

export default useResource;
